import React, { useState, useEffect, useRef, useCallback } from 'react';
import useSingingBowl from '../../hooks/useSingingBowl';
import useActivityTracker from '../../hooks/useActivityTracker';
import { getDailySankalp, getListByType } from '../../config/gathas';
import { useDrawer } from '../../contexts/DrawerContext';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BREATHING_PATTERNS = {
  simple: { inhale: 4, hold1: 0, exhale: 4, hold2: 0 },
  box:    { inhale: 4, hold1: 4, exhale: 4, hold2: 4 },
  '478':  { inhale: 4, hold1: 7, exhale: 8, hold2: 0 },
};

const SESSION_DURATION = 60;  // 1 minute
const CHIME_INTERVAL = 15;    // chime every 15 seconds
const SANKALP_DURATION = 30;  // daily sankalp: 30 seconds

const MODE_WEIGHTS = [
  { mode: 'gatha', weight: 35 },
  { mode: 'metta', weight: 20 },
  { mode: 'stretch', weight: 20 },
  { mode: 'eye', weight: 25 },
];

// ---------------------------------------------------------------------------
// MeditationIcon — seated meditation silhouette
// ---------------------------------------------------------------------------

function MeditationIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="3" r="2" />
      <path d="M10 5.5C8.5 7 7.5 8 6 9.5L4 12L5.5 13L8 10.5C7.5 13.5 7 15.5 7 17.5Q9 20.5 12 20.5Q15 20.5 17 17.5C17 15.5 16.5 13.5 16 10.5L18.5 13L20 12L18 9.5C16.5 8 15.5 7 14 5.5Z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

function pickWeightedMode(availableTypes) {
  const available = MODE_WEIGHTS.filter(m => availableTypes.includes(m.mode));
  if (available.length === 0) return 'gatha';
  const totalWeight = available.reduce((sum, m) => sum + m.weight, 0);
  let random = Math.random() * totalWeight;
  for (const m of available) {
    random -= m.weight;
    if (random <= 0) return m.mode;
  }
  return available[available.length - 1].mode;
}

function pickRandomPattern() {
  const keys = Object.keys(BREATHING_PATTERNS);
  return keys[Math.floor(Math.random() * keys.length)];
}

function computeBreathingPhase(elapsed, pattern) {
  const p = BREATHING_PATTERNS[pattern];
  const totalCycle = p.inhale + p.hold1 + p.exhale + p.hold2;
  const pos = elapsed % totalCycle;
  if (pos < p.inhale) return 'Inhale...';
  if (pos < p.inhale + p.hold1) return 'Hold...';
  if (pos < p.inhale + p.hold1 + p.exhale) return 'Exhale...';
  if (p.hold2 > 0) return 'Hold...';
  return 'Exhale...';
}

function getBreathingCycleDuration(pattern) {
  const p = BREATHING_PATTERNS[pattern];
  return p.inhale + p.hold1 + p.exhale + p.hold2;
}

function getLocalDateString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// MeditationBell — "The Inner Buddha"
// ---------------------------------------------------------------------------

export default function MeditationBell({ bottomOffset = 24 }) {
  // --- State (affects UI) ---
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [sessionMode, setSessionMode] = useState(null);
  const [currentContent, setCurrentContent] = useState(null);
  const [breathingPhase, setBreathingPhase] = useState('');
  const [showBeWell, setShowBeWell] = useState(false);
  const [longPressing, setLongPressing] = useState(false);

  // --- Refs (no re-renders) ---
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);
  const breathingPatternRef = useRef('simple');
  const contentListRef = useRef([]);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const sessionDurationRef = useRef(SESSION_DURATION);
  const sessionModeRef = useRef(null);
  const autoCloseTimerRef = useRef(null);
  const fadeTimerRef = useRef(null);
  const mountedRef = useRef(true);

  // --- Hooks ---
  const { strike, doubleStrike, cleanup } = useSingingBowl();
  const {
    showEyeReminder,
    showPauseSuggestion,
    dismissEyeReminder,
    dismissPauseSuggestion,
    eyeCountdown,
  } = useActivityTracker();
  const { isDrawerOpen } = useDrawer(); // counter-based: true when any drawer is open

  // -------------------------------------------------------------------------
  // clearAllTimers — single place to clean up everything
  // -------------------------------------------------------------------------

  const clearAllTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoCloseTimerRef.current) { clearTimeout(autoCloseTimerRef.current); autoCloseTimerRef.current = null; }
    if (fadeTimerRef.current) { clearTimeout(fadeTimerRef.current); fadeTimerRef.current = null; }
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
  }, []);

  // -------------------------------------------------------------------------
  // closeOverlay
  // -------------------------------------------------------------------------

  const closeOverlay = useCallback(() => {
    clearAllTimers();

    setIsClosing(true);
    fadeTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setIsOpen(false);
      setIsClosing(false);
      setSessionMode(null);
      setCurrentContent(null);
      setShowBeWell(false);
      setBreathingPhase('');
      sessionModeRef.current = null;
    }, 400);
  }, [clearAllTimers]);

  // -------------------------------------------------------------------------
  // startTimer — single consolidated 1s interval
  // -------------------------------------------------------------------------

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      if (!mountedRef.current) return;

      elapsedRef.current += 1;
      const elapsed = elapsedRef.current;
      const duration = sessionDurationRef.current;

      // Session complete
      if (elapsed >= duration) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        doubleStrike(0.25);
        setShowBeWell(true);
        // Auto-close after 2 seconds
        autoCloseTimerRef.current = setTimeout(() => {
          if (mountedRef.current) closeOverlay();
        }, 2000);
        return;
      }

      // Chime every 15 seconds
      if (elapsed % CHIME_INTERVAL === 0) {
        strike(0.15);
      }

      // Advance content every 15 seconds (for gatha, metta, eye)
      if (elapsed % CHIME_INTERVAL === 0 && elapsed > 0) {
        const list = contentListRef.current;
        if (list && list.length > 1) {
          const nextStep = Math.floor(elapsed / CHIME_INTERVAL) % list.length;
          setCurrentContent(list[nextStep]);
        }
      }

      // Update breathing phase only for modes that show it
      const mode = sessionModeRef.current;
      if (mode === 'gatha' || mode === 'metta' || mode === 'sankalp') {
        const pattern = breathingPatternRef.current;
        if (pattern) {
          setBreathingPhase(computeBreathingPhase(elapsed, pattern));
        }
      }
    }, 1000);
  }, [closeOverlay, doubleStrike, strike]);

  // -------------------------------------------------------------------------
  // startRandomSession
  // -------------------------------------------------------------------------

  async function startRandomSession() {
    if (!mountedRef.current) return;

    // Determine available types based on cached data
    const [gathas, mettas, stretches, eyes] = await Promise.all([
      getListByType('gatha'),
      getListByType('metta'),
      getListByType('desk_stretch'),
      getListByType('eye_exercise'),
    ]);

    if (!mountedRef.current) return;

    const available = [];
    if (gathas.length > 0) available.push('gatha');
    if (mettas.length > 0) available.push('metta');
    if (stretches.length > 0) available.push('stretch');
    if (eyes.length > 0) available.push('eye');

    const mode = pickWeightedMode(available);

    let content, contentList;
    if (mode === 'gatha') {
      contentList = gathas;
      content = gathas[Math.floor(Math.random() * gathas.length)];
    } else if (mode === 'metta') {
      contentList = mettas;
      content = mettas[0];
    } else if (mode === 'stretch') {
      contentList = stretches;
      content = stretches[Math.floor(Math.random() * stretches.length)];
    } else if (mode === 'eye') {
      contentList = eyes;
      content = eyes[0];
    }

    contentListRef.current = contentList;
    breathingPatternRef.current = pickRandomPattern();
    sessionDurationRef.current = SESSION_DURATION;
    sessionModeRef.current = mode;
    elapsedRef.current = 0;

    setSessionMode(mode);
    setCurrentContent(content);
    setShowBeWell(false);
    setIsClosing(false);
    setIsOpen(true);

    // Opening bell
    strike(0.3);

    // Start consolidated timer
    startTimer();
  }

  // -------------------------------------------------------------------------
  // FAB click handler (guards against long-press)
  // -------------------------------------------------------------------------

  function handleFabClick() {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    startRandomSession();
  }

  // -------------------------------------------------------------------------
  // Long-press handlers
  // -------------------------------------------------------------------------

  function handleLongPressStart() {
    longPressFiredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      strike(0.2);
      setLongPressing(true);
      setTimeout(() => setLongPressing(false), 300);
    }, 500);
  }

  function handleLongPressEnd() {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }

  // -------------------------------------------------------------------------
  // Daily Sankalp — auto on first visit of day
  // -------------------------------------------------------------------------

  useEffect(() => {
    const today = getLocalDateString();
    let last = null;
    try { last = localStorage.getItem('neev_lastSankalpDate'); } catch {}
    if (last === today) return;

    // Delayed to avoid blocking initial render
    const timer = setTimeout(async () => {
      if (!mountedRef.current) return;
      const sankalp = await getDailySankalp();
      if (!sankalp || !mountedRef.current) return;

      try { localStorage.setItem('neev_lastSankalpDate', today); } catch {}

      contentListRef.current = [sankalp];
      breathingPatternRef.current = 'simple';
      sessionDurationRef.current = SANKALP_DURATION;
      sessionModeRef.current = 'sankalp';
      elapsedRef.current = 0;

      setSessionMode('sankalp');
      setCurrentContent(sankalp);
      setShowBeWell(false);
      setIsClosing(false);
      setIsOpen(true);

      strike(0.3);
      startTimer();
    }, 2000);

    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  // -------------------------------------------------------------------------
  // Cleanup on unmount
  // -------------------------------------------------------------------------

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearAllTimers();
      cleanup();
    };
  }, [clearAllTimers, cleanup]);

  // -------------------------------------------------------------------------
  // Escape key to close
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') closeOverlay(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closeOverlay]);

  // -------------------------------------------------------------------------
  // Lock body scroll
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (isOpen && !isClosing) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen, isClosing]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      {/* FAB — Floating Bell Button - Hidden when any drawer is open */}
      {!isOpen && !isDrawerOpen && (
        <div className="fixed z-[35]" style={{ bottom: bottomOffset, right: 24 }}>
          {/* Eye care tooltip */}
          {showEyeReminder && (
            <div
              className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-white/10 backdrop-blur-md rounded-lg text-white/70 text-xs max-w-[200px] text-center animate-slideUp bell-breathe cursor-pointer"
              onClick={dismissEyeReminder}
              role="status"
              aria-live="polite"
            >
              Look 20 feet away for {eyeCountdown}s
            </div>
          )}

          {/* Pause suggestion tooltip */}
          {showPauseSuggestion && !showEyeReminder && (
            <div
              className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-white/10 backdrop-blur-md rounded-lg text-white/70 text-xs max-w-[200px] text-center bell-breathe cursor-pointer"
              onClick={() => { dismissPauseSuggestion(); startRandomSession(); }}
              role="status"
            >
              Take a mindful pause?
            </div>
          )}

          <button
            onClick={handleFabClick}
            onMouseDown={handleLongPressStart}
            onMouseUp={handleLongPressEnd}
            onMouseLeave={handleLongPressEnd}
            onTouchStart={handleLongPressStart}
            onTouchEnd={handleLongPressEnd}
            onTouchMove={handleLongPressEnd}
            className={`bg-white/70 dark:bg-gray-800/70 backdrop-blur-md shadow-lg rounded-full w-12 h-12 flex items-center justify-center text-gray-600 dark:text-gray-300 bell-breathe hover:shadow-xl transition-all ${longPressing ? 'scale-[1.2]' : ''}`}
            aria-label="Open meditation bell"
            title="Mindful pause"
          >
            <MeditationIcon size={22} />
          </button>
        </div>
      )}

      {/* Meditation Overlay */}
      {isOpen && (
        <div
          className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center ${isClosing ? 'meditation-fade-out' : 'meditation-fade-in'}`}
          style={{
            background: 'linear-gradient(180deg, rgba(30,27,75,0.96) 0%, rgba(15,23,42,0.97) 50%, rgba(41,37,36,0.96) 100%)',
          }}
          role="dialog"
          aria-modal="true"
          aria-label={sessionMode === 'sankalp' ? "Today's Sankalp" : 'Meditation session'}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeOverlay();
          }}
        >
          {/* Sankalp header */}
          {sessionMode === 'sankalp' && (
            <p className="text-white/50 text-xs uppercase tracking-widest mb-4">Today's Sankalp</p>
          )}

          {/* Meditation Silhouette + Aura — shown for gatha, metta, sankalp modes */}
          {(sessionMode === 'gatha' || sessionMode === 'metta' || sessionMode === 'sankalp') && (
            <div className="flex flex-col items-center mb-6">
              <div className="relative flex items-center justify-center" style={{ width: 170, height: 170 }}>
                {/* Breathing aura — expands/contracts behind the still figure */}
                <div
                  className="absolute breathing-aura"
                  style={{
                    width: 170, height: 170,
                    animationDuration: `${getBreathingCycleDuration(breathingPatternRef.current || 'simple')}s`,
                  }}
                />
                {/* Static meditation silhouette — the meditator is still, the aura breathes */}
                <div className="relative z-10 text-white/60">
                  <MeditationIcon size={100} />
                </div>
              </div>
              {/* Breathing phase cue — below the figure */}
              {breathingPhase && (
                <p className="text-white/40 text-xs font-light tracking-wider mt-2">{breathingPhase}</p>
              )}
            </div>
          )}

          {/* Stretch/Eye image (if available) */}
          {(sessionMode === 'stretch' || sessionMode === 'eye') && currentContent?.image_url && (
            <img
              src={currentContent.image_url}
              alt={currentContent.text_en || 'Exercise illustration'}
              className="rounded-lg opacity-90 mb-6 max-w-[200px] max-h-[200px] object-contain"
            />
          )}

          {/* Content text */}
          {currentContent && currentContent.text_en && (
            <>
              <p className="text-white/70 text-xs sm:text-sm italic text-center max-w-[280px] sm:max-w-xs leading-relaxed px-4 mb-2 whitespace-pre-line">
                "{currentContent.text_en}"
              </p>
              {currentContent.text_mr && (
                <p className="text-white/50 text-xs italic text-center max-w-[280px] sm:max-w-xs leading-relaxed px-4 mb-2">
                  "{currentContent.text_mr}"
                </p>
              )}
              {currentContent.source ? (
                <p className="text-white/30 text-[10px] text-center mb-6">— {currentContent.source}</p>
              ) : (
                <div className="mb-6" />
              )}
            </>
          )}

          {/* "Be well." on session end */}
          {showBeWell && (
            <p className="text-white/60 text-sm italic mb-8 motion-safe:animate-pulse">Be well.</p>
          )}

          {/* Sankalp "Begin My Day" button */}
          {sessionMode === 'sankalp' && !showBeWell && (
            <button
              onClick={closeOverlay}
              className="text-white/60 text-sm border border-white/20 rounded-full px-4 py-1.5 hover:bg-white/10 transition-colors mt-2 mb-4"
            >
              Begin My Day
            </button>
          )}

          {/* End / Close Button */}
          {!showBeWell && sessionMode !== 'sankalp' && (
            <button onClick={closeOverlay} className="text-white/40 text-sm hover:text-white/60 transition-colors mt-4">
              End
            </button>
          )}
        </div>
      )}
    </>
  );
}
